import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

interface ProjectConfigContextType {
  isConfigModalOpen: boolean;
  openConfigModal: (initialTab?: number, projectId?: string) => void;
  closeConfigModal: () => void;
  initialTab: number;
  contextProjectId: string | undefined;
}

const ProjectConfigContext = createContext<ProjectConfigContextType | undefined>(undefined);

export const useProjectConfig = () => {
  const context = useContext(ProjectConfigContext);
  if (context === undefined) {
    throw new Error('useProjectConfig must be used within a ProjectConfigProvider');
  }
  return context;
};

interface ProjectConfigProviderProps {
  children: ReactNode;
}

export const ProjectConfigProvider: React.FC<ProjectConfigProviderProps> = ({ children }) => {
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [initialTab, setInitialTab] = useState(0);
  const [contextProjectId, setContextProjectId] = useState<string | undefined>(undefined);
  const openConfigModal = useCallback((tab = 0, projectId?: string) => {
    setInitialTab(tab);
    setContextProjectId(projectId);
    setIsConfigModalOpen(true);
  }, []);

  const closeConfigModal = useCallback(() => {
    setIsConfigModalOpen(false);
    setContextProjectId(undefined);
  }, []);

  const value = useMemo(
    () => ({
      isConfigModalOpen,
      openConfigModal,
      closeConfigModal,
      initialTab,
      contextProjectId,
    }),
    [isConfigModalOpen, openConfigModal, closeConfigModal, initialTab, contextProjectId],
  );

  return (
    <ProjectConfigContext.Provider value={value}>
      {children}
    </ProjectConfigContext.Provider>
  );
};
